import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowUpCircle,
  ArrowDownCircle,
  Eye,
  Edit,
  Package,
  Layers,
  MapPin,
  Calendar,
  AlertTriangle,
  X,
  Barcode,
} from "lucide-react";
import { useBarcodeGlobal } from "@/contexts/BarcodeGlobalContext";
import { StockMovementDialog } from "@/components/Inventory/StockMovementDialog";
import { RawMaterialMovementDialog } from "@/components/Inventory/RawMaterialMovementDialog";
import type { MaterialInventory, RawMaterial } from "@/types/legacy";
import { cn } from "@/lib/utils";

export function GlobalBarcodeActionPanel() {
  const navigate = useNavigate();
  const { state, closePanel } = useBarcodeGlobal();
  const { showPanel, lastResult, isSearching, error } = state;

  const [movementDialog, setMovementDialog] = useState<{
    open: boolean;
    type: "in" | "out" | "adjustment";
  }>({ open: false, type: "in" });

  // Get material data
  const material = lastResult?.material;
  const rawMaterial = lastResult?.rawMaterial;
  const source = lastResult?.source;
  const found = lastResult?.found;

  // Common data extraction
  const name = material?.name || rawMaterial?.name || "";
  const code = material?.code || rawMaterial?.code || lastResult?.scannedCode || "";
  const currentStock = material?.current_stock || rawMaterial?.quantity || 0;
  const unit = material?.unit || rawMaterial?.unit || "units";
  const batchNumber = material?.batch_number || rawMaterial?.batch_number;
  const rollNumber = material?.roll_number || rawMaterial?.roll_number;
  const location = material?.location || rawMaterial?.location;
  const expiryDate = material?.expiry_date || rawMaterial?.expiry_date;
  const minStock = material?.min_stock || 0;
  const squareMeters = material?.square_meters || rawMaterial?.square_meters;
  const isFreezer = material?.is_freezer_material || rawMaterial?.is_freezer_material;

  const isLowStock = currentStock < minStock;

  const handleViewDetails = () => {
    closePanel();
    if (source === "material_inventory") {
      navigate("/materials");
    } else {
      navigate("/materials");
    }
  };

  const handleMovement = (type: "in" | "out") => {
    setMovementDialog({ open: true, type });
  };

  const handleMovementClose = () => {
    setMovementDialog({ open: false, type: "in" });
  };

  const handleMovementSuccess = () => {
    handleMovementClose();
    closePanel();
  };

  return (
    <>
      <Sheet open={showPanel} onOpenChange={(open) => !open && closePanel()}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Barcode className="w-5 h-5" />
              {isSearching ? "Buscando..." : found ? "Material Encontrado" : "Material No Encontrado"}
            </SheetTitle>
            <SheetDescription>
              Codigo escaneado: <code className="font-mono bg-muted px-1 rounded">{code}</code>
            </SheetDescription>
          </SheetHeader>

          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : !found ? (
            <div className="py-8 text-center space-y-4">
              <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500" />
              <p className="text-muted-foreground">
                No se encontro ningun material con el codigo <strong>{code}</strong>
              </p>
              <Button variant="outline" onClick={closePanel}>
                Cerrar
              </Button>
            </div>
          ) : (
            <div className="space-y-6 mt-6">
              {/* Material Info */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {source === "material_inventory" ? "Inventario" : "Materia Prima"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {isFreezer && (
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                        Congelador
                      </Badge>
                    )}
                    {isLowStock && (
                      <Badge variant="destructive">Stock Bajo</Badge>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Stock Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                      <Package className="w-4 h-4" />
                      Stock Actual
                    </div>
                    <p className={cn(
                      "text-2xl font-bold",
                      isLowStock && "text-red-600"
                    )}>
                      {currentStock} <span className="text-sm font-normal">{unit}</span>
                    </p>
                  </div>

                  {squareMeters !== undefined && squareMeters > 0 && (
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                        <Layers className="w-4 h-4" />
                        Metros Cuadrados
                      </div>
                      <p className="text-2xl font-bold">
                        {squareMeters} <span className="text-sm font-normal">m2</span>
                      </p>
                    </div>
                  )}
                </div>

                {/* Lot/Roll Info */}
                {(batchNumber || rollNumber) && (
                  <div className="p-3 rounded-lg border space-y-2">
                    {batchNumber && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Lote:</span>
                        <span className="font-medium">{batchNumber}</span>
                      </div>
                    )}
                    {rollNumber && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Rollo:</span>
                        <span className="font-medium">{rollNumber}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Location */}
                {location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{location}</span>
                  </div>
                )}

                {/* Expiry */}
                {expiryDate && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>Vence: {new Date(expiryDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleMovement("in")}
                  >
                    <ArrowUpCircle className="w-4 h-4 mr-2" />
                    Entrada
                  </Button>
                  <Button
                    className="bg-red-600 hover:bg-red-700"
                    onClick={() => handleMovement("out")}
                  >
                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                    Salida
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button variant="outline" onClick={handleViewDetails}>
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Detalles
                  </Button>
                  <Button variant="outline" onClick={handleViewDetails}>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </div>

                <Button variant="ghost" className="w-full" onClick={closePanel}>
                  <X className="w-4 h-4 mr-2" />
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Movement Dialogs */}
      {source === "material_inventory" && material && (
        <StockMovementDialog
          open={movementDialog.open}
          onOpenChange={(open) => !open && handleMovementClose()}
          materialId={material.id}
          materialName={material.name}
          currentStock={material.current_stock}
          unit={material.unit}
          movementType={movementDialog.type}
        />
      )}

      {source === "raw_materials" && rawMaterial && (
        <RawMaterialMovementDialog
          open={movementDialog.open}
          onOpenChange={(open) => !open && handleMovementClose()}
          materialId={rawMaterial.id}
          materialName={rawMaterial.name}
          currentStock={rawMaterial.quantity}
          currentSquareMeters={rawMaterial.square_meters}
          unit={rawMaterial.unit}
          movementType={movementDialog.type}
          onSuccess={handleMovementSuccess}
        />
      )}
    </>
  );
}
